import React, { useState } from "react";
import PaginationUI from "../atoms/PaginationUI";

type ColumnConfig<T> = {
  accessor: (row: T) => any;
  Header: string;
  header: string;
  render?: any;
  // Optional: Priority for mobile display (1 = highest priority)
  priority?: number;
  // Optional: Minimum width for column
  minWidth?: string;
};

type TableProps<T> = {
  data: T[];
  columns: ColumnConfig<T>[];
  renderHeaderExtras?: () => React.ReactNode;
  navigate_item?: string;
};

const Table = <T,>({
  data,
  columns,
  renderHeaderExtras,
  navigate_item,
}: TableProps<T>) => {
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const paginatedData = data?.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="flow-root">
        {/* Header Extras */}
        {renderHeaderExtras && (
          <div
            className="mt-3 px-3 py-3.5"
            style={{
              display: "flex",
              justifyContent: "flex-end",
              width: "100%",
            }}
          >
            {renderHeaderExtras()}
          </div>
        )}
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full align-middle">
            <div className="relative max-h-[60vh] overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    {columns?.map((column, index) => (
                      <th
                        key={index}
                        scope="col"
                        className={`
                           border-b border-gray-300 
                          bg-white bg-opacity-75 px-3 py-3.5 
                          text-sm font-semibold text-gray-900 
                          backdrop-blur backdrop-filter
                          ${index === columns?.length - 1 ? "text-right" : "text-left"}
                          ${column.minWidth || "min-w-[120px]"}
                          ${column.priority && column.priority > 1 ? "hidden sm:table-cell" : ""}
                        `}
                      >
                        {column.header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {paginatedData?.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {columns?.map((column, colIndex) => (
                        <td
                          key={colIndex}
                          className={`
                            px-3 py-4 text-sm text-gray-500
                            ${colIndex === columns?.length - 1 ? "text-right" : "text-left"}
                            ${column.minWidth || "min-w-[120px]"}
                            ${column.priority && column.priority > 1 ? "hidden sm:table-cell" : ""}
                          `}
                        >
                          {column.render
                            ? column.render(row, rowIndex, data)
                            : typeof column.accessor === "function"
                              ? column.accessor(row)
                              : row[column.accessor as keyof T]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex justify-center sm:justify-end mb-10">
          <PaginationUI
            currentPage={currentPage}
            totalPages={Math.ceil(data?.length / pageSize)}
            onPageChange={setCurrentPage}
            pageSize={pageSize}
            totalResults={data?.length}
          />
        </div>
      </div>
    </div>
  );
};

export default Table;
